import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateTimeBoth } from '../utils/dates';
import { humanSize } from '../utils/files';
import { REQ_CATEGORY_AR, REQ_STATUS_AR, REQ_STATUS_BADGE } from '../utils/requests';
import { PRIORITY_AR } from '../utils/labels';
import RequestAttachments from '../components/RequestAttachments';
import ClientMessagesThread from '../components/ClientMessagesThread';


const STEPS = [
  { key: 'new', label: 'استلام التذكرة' },
  { key: 'assigned', label: 'جارٍ العمل' },
  { key: 'completed', label: 'الإنجاز' },
  { key: 'closed', label: 'الإغلاق' },
];

const emptyReq = { title: '', description: '', priority: 'medium', category: '', project_id: '' };



function Timeline({ request }) {
  const order = ['new', 'assigned', 'completed', 'closed'];
  // انتظار الرد لا يُعدّ مرحلة مستقلة: التذكرة واقفة عند مرحلتها الحالية
  const stage = request.status === 'awaiting_client'
    ? (request.task_id ? 'assigned' : 'new')
    : request.status;
  const current = request.status === 'rejected' ? -1 : order.indexOf(stage);

  if (request.status === 'rejected') {
    return <div style={{ fontSize: 13, color: 'var(--danger)' }}>اعتُذر عن هذه التذكرة.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {STEPS.map((s, i) => (
          <span key={s.key} style={{
            fontSize: 12, padding: '3px 10px', borderRadius: 999,
            background: i <= current ? 'var(--primary, var(--c-blue))' : 'var(--border)',
            color: i <= current ? '#fff' : 'var(--text-muted)',
          }}>{s.label}</span>
        ))}
      </div>
      {request.status === 'awaiting_client' && (
        <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>
          ⏳ فريق العمل بانتظار ردّكم — أجيبوا من خانة المحادثة أدناه لنكمل.
        </div>
      )}
    </div>
  );
}

function Stars({ value, onPick, size = 26 }) {
  return (
    <div style={{ display: 'flex', gap: 4, direction: 'ltr' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} role={onPick ? 'button' : undefined} onClick={onPick ? () => onPick(n) : undefined}
          title={onPick ? `${n} من 5` : undefined}
          style={{ fontSize: size, lineHeight: 1, cursor: onPick ? 'pointer' : 'default', color: n <= value ? 'var(--c-warning)' : 'var(--border-strong)' }}>
          ★
        </span>
      ))}
    </div>
  );
}

export default function ClientRequests() {
  const { api, apiUpload } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileRef = useRef(null);

  const statusFilter = searchParams.get('status') || '';

  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyReq);
  const [saving, setSaving] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [replyNote, setReplyNote] = useState('');
  // مرفقات وروابط تُجهَّز قبل إرسال التذكرة ثم تُرفع بعد إنشائها
  const [draftFiles, setDraftFiles] = useState([]);
  const [draftLinks, setDraftLinks] = useState([]);
  const [draftLinkUrl, setDraftLinkUrl] = useState('');
  const [draftLinkName, setDraftLinkName] = useState('');
  const draftFileRef = useRef(null);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  const load = () => {
    const qs = statusFilter ? `?status=${statusFilter}` : '';
    api(`/client-requests${qs}`).then(setRequests).catch(() => {}).finally(() => setLoading(false));
  };
  const loadQuota = () => api('/client-requests/storage').then(setQuota).catch(() => {});
  const reloadSelected = (reqId) => api(`/client-requests/${reqId}`).then(setSelected).catch(() => {});

  const openRequest = (reqId) => {
    setReplyNote('');
    navigate({ pathname: `/portal/requests/${reqId}`, search: searchParams.toString() });
  };
  const closeRequest = () => navigate({ pathname: '/portal/requests', search: searchParams.toString() });

  useEffect(load, [statusFilter]);
  useEffect(() => {
    if (!id) { setSelected(null); return; }
    api(`/client-requests/${id}`).then(setSelected).catch(() => setSelected(null));
  }, [id]);
  useEffect(() => {
    setRating(selected?.rating || 0);
    setRatingComment(selected?.rating_comment || '');
  }, [selected?.id, selected?.rating]);
  useEffect(() => {
    api('/projects').then(setProjects).catch(() => {});
    loadQuota();
  }, []);

  const addDraftFiles = (files) => {
    const picked = Array.from(files || []);
    const tooBig = quota && picked.find(f => f.size > quota.file_limit);
    if (tooBig) { toast.error(`"${tooBig.name}" أكبر من الحد الأقصى (${humanSize(quota.file_limit)})`); return; }
    setDraftFiles(prev => [...prev, ...picked]);
  };

  const addDraftLink = () => {
    if (!draftLinkUrl.trim()) return;
    setDraftLinks(prev => [...prev, { url: draftLinkUrl.trim(), name: draftLinkName.trim() || null }]);
    setDraftLinkUrl(''); setDraftLinkName('');
  };

  const resetForm = () => {
    setForm(emptyReq);
    setDraftFiles([]); setDraftLinks([]);
    setDraftLinkUrl(''); setDraftLinkName('');
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    if (projects.length > 0 && !form.project_id) { toast.warning('اختر المشروع المرتبط بالتذكرة'); return; }
    setSaving(true);
    try {
      const created = await api('/client-requests', {
        method: 'POST',
        body: JSON.stringify({ ...form, project_id: form.project_id || null }),
      });

      // المرفقات تحتاج رقم التذكرة، فتُرفع فور إنشائها
      let attachError = null;
      if (draftFiles.length) {
        try {
          const fd = new FormData();
          draftFiles.forEach(f => fd.append('files[]', f));
          await apiUpload(`/client-requests/${created.id}/attachments`, fd);
        } catch (err) { attachError = err.message; }
      }
      for (const l of draftLinks) {
        try {
          await api(`/client-requests/${created.id}/links`, { method: 'POST', body: JSON.stringify(l) });
        } catch (err) { attachError = err.message; }
      }

      resetForm();
      setShowForm(false);
      if (attachError) toast.warning(`فُتحت التذكرة، لكن تعذّر إرفاق بعض الملفات: ${attachError}`);
      else toast.success('تم فتح تذكرتكم');
      load();
      loadQuota();
      openRequest(created.id);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const submitRating = async () => {
    if (!rating) { toast.warning('اختر عدد النجوم أولاً'); return; }
    try {
      const res = await api(`/client-requests/${selected.id}/rate`, {
        method: 'POST', body: JSON.stringify({ rating, comment: ratingComment.trim() || null }),
      });
      toast.success(res.message);
      load(); reloadSelected(selected.id);
    } catch (err) { toast.error(err.message); }
  };

  const upload = async (files) => {
    if (!files?.length || !selected) return;
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files[]', f));
      await apiUpload(`/client-requests/${selected.id}/attachments`, fd);
      toast.success('تم رفع المرفقات');
      reloadSelected(selected.id);
      loadQuota();
    } catch (err) { toast.error(err.message); }
  };

  const addLink = async () => {
    if (!linkUrl.trim()) return;
    try {
      await api(`/client-requests/${selected.id}/links`, {
        method: 'POST',
        body: JSON.stringify({ url: linkUrl.trim(), name: linkName.trim() || null }),
      });
      setLinkUrl(''); setLinkName('');
      toast.success('تمت إضافة المشاركة');
      reloadSelected(selected.id);
    } catch (err) { toast.error(err.message); }
  };

  const removeAttachment = async (a) => {
    if (!window.confirm(`حذف "${a.file_name}"؟`)) return;
    try {
      await api(`/client-requests/${selected.id}/attachments/${a.id}`, { method: 'DELETE' });
      reloadSelected(selected.id);
      loadQuota();
    } catch (err) { toast.error(err.message); }
  };

  const confirmDone = async () => {
    try {
      await api(`/client-requests/${selected.id}/confirm`, {
        method: 'POST', body: JSON.stringify({ feedback: replyNote || null }),
      });
      setReplyNote('');
      toast.success('تم إغلاق التذكرة. شكراً لكم');
      load(); reloadSelected(selected.id);
    } catch (err) { toast.error(err.message); }
  };

  const askRevision = async () => {
    if (!replyNote.trim()) { toast.warning('اكتب ملاحظتكم أولاً'); return; }
    try {
      await api(`/client-requests/${selected.id}/reopen`, {
        method: 'POST', body: JSON.stringify({ note: replyNote.trim() }),
      });
      setReplyNote('');
      toast.success('تم إرسال ملاحظتكم');
      load(); reloadSelected(selected.id);
    } catch (err) { toast.error(err.message); }
  };

  // مشروع واحد فقط ← يُختار تلقائياً
  const openForm = () => {
    resetForm();
    setForm({ ...emptyReq, project_id: projects.length === 1 ? String(projects[0].id) : '' });
    setShowForm(true);
  };

  const quotaPct = quota?.client_limit ? Math.min(100, Math.round((quota.used / quota.client_limit) * 100)) : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>تذاكري</h3>
        <button className="btn btn-primary btn-sm" onClick={openForm}>+ فتح تذكرة</button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <select className="form-select" style={{ maxWidth: 200 }} value={statusFilter}
          onChange={e => setSearchParams(e.target.value ? { status: e.target.value } : {}, { replace: true })}>
          <option value="">كل الحالات</option>
          {Object.entries(REQ_STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {quota && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            المساحة المستخدمة: {humanSize(quota.used)} من {humanSize(quota.client_limit)} ({quotaPct}%)
          </div>
        )}
      </div>

      {loading ? <div className="loader">جاري التحميل...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.4fr' : '1fr', gap: 16, alignItems: 'start' }}>
          <div>
            {requests.length === 0 ? (
              <div className="card"><div className="empty">لا توجد تذاكر بعد. افتح تذكرتك الأولى.</div></div>
            ) : requests.map(r => (
              <div key={r.id} className="card" style={{ marginBottom: 8, cursor: 'pointer', borderRight: selected?.id === r.id ? '3px solid var(--primary)' : undefined }}
                onClick={() => openRequest(r.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 500 }}>
                    <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>#{r.id}</span> {r.title}
                  </div>
                  <span className={`badge badge-${REQ_STATUS_BADGE[r.status] || 'pending'}`}>{REQ_STATUS_AR[r.status] || r.status}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>
                  {r.project?.title && <span>📁 {r.project.title} • </span>}
                  {r.category && <span>{REQ_CATEGORY_AR[r.category]} • </span>}
                  <span>⚡ {PRIORITY_AR[r.priority] || r.priority}</span>
                  <span> • فُتحت: {fmtDateTimeBoth(r.created_at)}</span>
                  {r.attachments?.length > 0 && <span> • 📎 {r.attachments.length}</span>}
                </div>
                {r.unread_messages > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>
                    💬 ردّ جديد من فريق العمل ({r.unread_messages})
                  </div>
                )}
                {r.rating > 0 && (
                  <div style={{ marginTop: 6 }}><Stars value={r.rating} size={14} /></div>
                )}
              </div>
            ))}
          </div>

          {selected && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <h3 style={{ margin: 0 }}><span style={{ color: 'var(--text-faint)', fontSize: 14 }}>#{selected.id}</span> {selected.title}</h3>
                <button className="btn-icon" onClick={closeRequest} title="إغلاق">✕</button>
              </div>
              <Timeline request={selected} />

              {selected.description && <p style={{ marginTop: 12, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{selected.description}</p>}

              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                {selected.project?.title && <div>📁 المشروع: {selected.project.title}</div>}
                {selected.handler?.name && <div>👤 المسؤول: {selected.handler.name}</div>}
                <div>🕒 فُتحت: {fmtDateTimeBoth(selected.created_at)}</div>
                {selected.closed_at && <div>✅ أُغلقت: {fmtDateTimeBoth(selected.closed_at)}</div>}
              </div>

              {selected.status === 'completed' && selected.auto_close_at && (
                <div style={{ marginTop: 16, padding: 14, background: 'var(--c-gold-soft)', borderRadius: 8 }}>
                  <div style={{ fontWeight: 500, marginBottom: 6 }}>تم إنجاز تذكرتكم</div>
                  <div style={{ fontSize: 13, color: 'var(--c-gold-ink)' }}>
                    يُرجى تأكيد الاستلام أو طلب تعديل قبل {fmtDateTimeBoth(selected.auto_close_at)}؛
                    وإلا ستُغلق التذكرة تلقائياً بعد يوم عمل واحد.
                  </div>
                  <textarea className="form-textarea" style={{ marginTop: 10, minHeight: 60 }}
                    placeholder="ملاحظاتكم (مطلوبة عند طلب التعديل)"
                    value={replyNote} onChange={e => setReplyNote(e.target.value)} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-success btn-sm" onClick={confirmDone}>✓ تأكيد الاستلام وإغلاق</button>
                    <button className="btn btn-outline btn-sm" onClick={askRevision}>✗ طلب تعديل</button>
                  </div>
                </div>
              )}

              {selected.admin_notes && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
                  <strong>ملاحظة الإدارة:</strong> {selected.admin_notes}
                </div>
              )}

              {(selected.rating || selected.ratable) && (
                <div style={{ marginTop: 16, padding: 14, background: 'var(--surface-alt, var(--surface-alt))', borderRadius: 8 }}>
                  <div style={{ fontWeight: 500, marginBottom: 6 }}>
                    {selected.rating ? 'تقييمكم للخدمة' : 'كيف كانت خدمتنا على هذه التذكرة؟'}
                  </div>
                  <Stars value={rating} onPick={setRating} />
                  <textarea className="form-textarea" style={{ marginTop: 10, minHeight: 60 }}
                    placeholder="ملاحظات على الخدمة (اختياري)"
                    value={ratingComment} onChange={e => setRatingComment(e.target.value)} />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={submitRating} disabled={!rating}>
                      {selected.rating ? 'تحديث التقييم' : 'إرسال التقييم'}
                    </button>
                    {selected.rated_at && (
                      <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>آخر تقييم: {fmtDateTimeBoth(selected.rated_at)}</span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 20 }}>
                <h4 style={{ marginBottom: 8 }}>المحادثة</h4>
                {selected.reopenable && (
                  <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                    التذكرة مغلقة، لكن يمكنكم الرد هنا حتى {fmtDateTimeBoth(selected.reopen_until)} فتُفتح من جديد بسياقها بدل تذكرة جديدة.
                  </div>
                )}
                <ClientMessagesThread requestId={selected.id} height={240}
                  readOnly={!(['new', 'awaiting_client', 'assigned', 'completed'].includes(selected.status) || selected.reopenable)}
                  onRead={load} />
              </div>

              <div style={{ marginTop: 20 }}>
                <h4 style={{ marginBottom: 8 }}>المرفقات</h4>
                <RequestAttachments items={selected.attachments} onRemove={selected.status !== 'closed' && selected.status !== 'rejected' ? removeAttachment : null} />

                {selected.status !== 'closed' && selected.status !== 'rejected' && (
                  <div style={{ marginTop: 12 }}>
                    <input type="file" ref={fileRef} multiple style={{ display: 'none' }}
                      onChange={e => { upload(e.target.files); e.target.value = ''; }} />
                    <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()}>📎 رفع ملف</button>
                    {quota && (
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>
                        حتى {humanSize(quota.file_limit)} لكل ملف، و{humanSize(quota.request_limit)} لكل تذكرة.
                        للملفات الكبيرة شارك رابطاً من قوقل درايف أو ون درايف.
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                      <input className="form-input" style={{ flex: '2 1 240px' }} placeholder="رابط مشاركة قوقل درايف أو ون درايف"
                        value={linkUrl} onChange={e => setLinkUrl(e.target.value)} />
                      <input className="form-input" style={{ flex: '1 1 140px' }} placeholder="اسم المشاركة (اختياري)"
                        value={linkName} onChange={e => setLinkName(e.target.value)} />
                      <button className="btn btn-outline btn-sm" onClick={addLink} disabled={!linkUrl.trim()}>إضافة رابط</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '86vh', overflowY: 'auto' }}>
            <div className="modal-title">فتح تذكرة جديدة</div>
            <div className="form-group">
              <label className="form-label">عنوان التذكرة *</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">التفاصيل</label>
              <textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">الأولوية</label>
                <select className="form-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">منخفضة</option>
                  <option value="medium">متوسطة</option>
                  <option value="high">عالية</option>
                  <option value="urgent">عاجلة</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">نوع الطلب</label>
                <select className="form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="">— اختر النوع —</option>
                  {Object.entries(REQ_CATEGORY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المشروع المرتبط{projects.length > 0 ? ' *' : ''}</label>
                <select className="form-select" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
                  <option value="">— اختر المشروع —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">المرفقات</label>
              <input type="file" ref={draftFileRef} multiple style={{ display: 'none' }}
                onChange={e => { addDraftFiles(e.target.files); e.target.value = ''; }} />
              <button className="btn btn-outline btn-sm" onClick={() => draftFileRef.current?.click()}>📎 اختيار ملفات</button>
              {quota && (
                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>
                  حتى {humanSize(quota.file_limit)} لكل ملف، و{humanSize(quota.request_limit)} لكل تذكرة.
                  الصيغ المسموحة: {quota.allowed_extensions?.join('، ')}
                </div>
              )}
              {draftFiles.length > 0 && (
                <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
                  {draftFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, background: 'var(--surface-alt, var(--surface-alt))', padding: '6px 10px', borderRadius: 6 }}>
                      <span>📄 {f.name} <span style={{ color: 'var(--text-faint)' }}>({humanSize(f.size)})</span></span>
                      <button className="btn-icon" title="إزالة"
                        onClick={() => setDraftFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">مشاركة من قوقل درايف أو ون درايف</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input className="form-input" style={{ flex: '2 1 220px' }} placeholder="رابط المشاركة"
                  value={draftLinkUrl} onChange={e => setDraftLinkUrl(e.target.value)} />
                <input className="form-input" style={{ flex: '1 1 130px' }} placeholder="الاسم (اختياري)"
                  value={draftLinkName} onChange={e => setDraftLinkName(e.target.value)} />
                <button className="btn btn-outline btn-sm" onClick={addDraftLink} disabled={!draftLinkUrl.trim()}>إضافة</button>
              </div>
              {draftLinks.length > 0 && (
                <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
                  {draftLinks.map((l, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, background: 'var(--surface-alt, var(--surface-alt))', padding: '6px 10px', borderRadius: 6 }}>
                      <span>🔗 {l.name || l.url}</span>
                      <button className="btn-icon" title="إزالة"
                        onClick={() => setDraftLinks(prev => prev.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              تُرفع المرفقات مع التذكرة، ويمكنكم إضافة المزيد لاحقاً من صفحة التذكرة.
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={submit} disabled={!form.title.trim() || saving || (projects.length > 0 && !form.project_id)}>
                {saving ? 'جارٍ الإرسال...' : 'إرسال التذكرة'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowForm(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
